/**
 * model/explode.ts — Layout della vista esplosa lungo world X (30-09-2026).
 *
 * Ordine richiesto dall'utente (da −X a +X, con uno spazio tra TUTTI gli
 * elementi):
 *
 * ```
 * trave header | lock header | - - - | vite header |  gap  | vite joist | - - - | lock joist | trave joist
 * ```
 *
 * Con lockstop sul lato (30-09-2026, richiesta utente) si aggiunge il suo
 * disaccoppiamento: `lock | gap | lockstop | gap | viti`, con la linea
 * tratteggiata che unisce foro, lockstop e vite.
 *
 * Il verso di ogni lato NON e' una scelta estetica: deriva da `beams.ts`
 * (header centrato a `x = −b/2` o `−h/2`, joist a `x = +l·cos(α)/2`), quindi
 * header esplode verso −X e joist verso +X. Il bug precedente usava i segni
 * opposti e le travi attraversavano i connettori.
 *
 * Distanze per lato (modulo, verso l'esterno del proprio lato), con
 * `G = gapMm`, `L` = vite piu' lunga del lato, `T` = spessore assieme,
 * `S` = spessore lockstop (0 se assente), `E = G` se c'e' lockstop, 0 altrimenti:
 * - lockstop: si stacca dal lock di `G` verso il centro;
 * - vite: arretra lungo −axis di `P = L + G + E` → la punta resta a `G` dalla
 *   faccia del lock, o dalla faccia interna del lockstop (la testa della vite
 *   coperta parte gia' spostata di `S`, vedi `LockScene.tsx`);
 * - lock: `C = P + S + G/2` → la testa della vite resta a `G/2` dalla
 *   posizione assemblata, quindi tra le teste dei due lati c'e' (almeno) `G`;
 * - trave: `B = C + T + G` → `G` oltre il dorso del lock, anche con notch
 *   (lock incassato nella trave per al piu' `T`).
 *
 * Puro: nessuna posizione del model cambia, il render layer compone gli offset.*/

import type { ConnectorSide, Vec3 } from "../data/types";
import type { LockstopInstance } from "./lockstopPlacement";
import type { FastenerAnchor, ResolvedScene } from "./types";

export interface ExplodeSideInput {
  /** Lunghezza della vite piu' lunga del lato (mm). */
  fastenerLengthMm: number;
  /** Spessore dell'assieme connettore lungo X (mm). */
  connectorThicknessMm: number;
  /** Spessore del lockstop sul lato (mm), 0 se il lato non ha lockstop. */
  lockstopThicknessMm: number;
}

export interface ExplodeSideLayout {
  /** Verso di esplosione lungo world X: header −1, joist +1. */
  sign: -1 | 1;
  /** Offset X della trave (mm, con segno). */
  beamOffsetXMm: number;
  /** Offset X del lock (mm, con segno) — ereditato dalle viti del lato. */
  connectorOffsetXMm: number;
  /** Offset X del lockstop (mm, con segno): quello del lock meno `G` verso il centro. */
  lockstopOffsetXMm: number;
  /** Arretramento della vite lungo −axis (mm, modulo). */
  fastenerPullBackMm: number;
}

/** Verso di esplosione di un lato lungo world X (vedi doc-comment del file). */
export function explodeSignFor(side: ConnectorSide): -1 | 1 {
  return side === "header" ? -1 : 1;
}

/**
 * Layout esploso di un lato, scalato da `factor` (0 = assemblato, 1 = esploso).
 *
 * @param side Lato ("header" | "joist").
 * @param input Ingombri reali del lato (vite piu' lunga, spessore assieme, lockstop).
 * @param gapMm Spazio minimo tra elementi consecutivi a `factor = 1`.
 * @param factor Fattore di esplosione 0..1.*/
export function explodeLayoutFor(
  side: ConnectorSide,
  input: ExplodeSideInput,
  gapMm: number,
  factor: number,
): ExplodeSideLayout {
  const sign = explodeSignFor(side);
  const hasLockstop = input.lockstopThicknessMm > 0;
  const pullBack = input.fastenerLengthMm + gapMm + (hasLockstop ? gapMm : 0);
  const connector = pullBack + input.lockstopThicknessMm + gapMm / 2;
  const lockstop = connector - gapMm;
  const beam = connector + input.connectorThicknessMm + gapMm;
  return {
    sign,
    beamOffsetXMm: sign * factor * beam,
    connectorOffsetXMm: sign * factor * connector,
    lockstopOffsetXMm: sign * factor * lockstop,
    fastenerPullBackMm: factor * pullBack,
  };
}

/** Layout esploso dei due lati. */
export type ExplodeLayout = Record<ConnectorSide, ExplodeSideLayout>;

/**
 * Layout esploso di entrambi i lati a partire dalla scena risolta: ingombri
 * reali per lato (vite piu' lunga, spessore assieme, lockstop presente).
 * Usato dal viewer (`LockScene.tsx`) e dalla capture del report
 * (`LockSceneCapture.tsx`) — una sola regola per entrambi.
 *
 * @param scene Scena risolta.
 * @param lockstops Istanze lockstop (definiscono quali lati hanno il lockstop).
 * @param lockstopThicknessMm Spessore lockstop (misurato vicino ai fori).
 * @param gapMm Spazio minimo tra elementi consecutivi.
 * @param factor Fattore di esplosione 0..1.*/
export function explodeLayoutForScene(
  scene: Pick<ResolvedScene, "fasteners" | "connectors">,
  lockstops: readonly Pick<LockstopInstance, "side">[],
  lockstopThicknessMm: number,
  gapMm: number,
  factor: number,
): ExplodeLayout {
  const sideLayout = (side: ConnectorSide) => {
    const fastenerLengthMm = Math.max(0, ...scene.fasteners.filter((f) => f.side === side).map((f) => f.lengthMm));
    const connectorThicknessMm = Math.max(
      0,
      ...scene.connectors.filter((c) => c.side === side).map((c) => c.assembly.thickness),
    );
    const sideHasLockstop = lockstops.some((l) => l.side === side);
    return explodeLayoutFor(
      side,
      { fastenerLengthMm, connectorThicknessMm, lockstopThicknessMm: sideHasLockstop ? lockstopThicknessMm : 0 },
      gapMm,
      factor,
    );
  };
  return { header: sideLayout("header"), joist: sideLayout("joist") };
}

/**
 * Punti esplosi di una vite (prima della correzione bbox del connettore):
 * - `screw`: testa della vite — segue il lock del proprio lato (anche le
 *   inclinate, il cui foro e' sul lock joist) e arretra lungo −axis;
 * - `hole`: foro sulla faccia del lock esploso, da `anchor.position` e NON da
 *   `headPosition` (gia' spostata dello spessore lockstop), cosi' la linea
 *   guida parte dal lock e attraversa lockstop e vite.
 *
 * @param anchor Ancoraggio vite.
 * @param headPosition Testa assemblata (anchor.position + eventuale spessore lockstop).
 * @param layout Layout del lato della vite.*/
export function explodedFastenerPoints(
  anchor: Pick<FastenerAnchor, "position" | "axis">,
  headPosition: Vec3,
  layout: ExplodeSideLayout,
): { screw: Vec3; hole: Vec3 } {
  const pull = layout.fastenerPullBackMm;
  return {
    screw: [
      headPosition[0] + layout.connectorOffsetXMm - anchor.axis[0] * pull,
      headPosition[1] - anchor.axis[1] * pull,
      headPosition[2] - anchor.axis[2] * pull,
    ],
    hole: [anchor.position[0] + layout.connectorOffsetXMm, anchor.position[1], anchor.position[2]],
  };
}
